/**
 * Slovenian — every string this portal says.
 *
 * The staff vocabulary is taken WORD FOR WORD from `pms-web` and `pms-housekeeping`
 * wherever they already had one: „Moj urnik“, „Potrjujem“, „Prihod“, „Presežek — čaka
 * razvrstitev“. That is deliberate rather than lazy, and it is the same argument
 * `HousekeepingVocabulary` makes on the service side — the telephone, the letter and
 * this portal must not call one thing by three names, and a person who reads „Potrjujem“
 * on their supervisor's screen and something else on their own has two features as far
 * as they are concerned.
 *
 * What is new here is what this portal has that no other does: the home screen, the
 * personnel card as its owner reads it, „ostani prijavljen“, and the two sentences that
 * say what this portal deliberately cannot do — swap a shift, and request any absence but
 * leave (docs/OSEBJE.md §10, §18).
 */
export default {
    app: {
        name: "Zaposleni",
    },
    errors: {
        conflict: "Zapis je že zaseden ali obstaja",
        generic_error: "Prišlo je do napake",
        invalid_data: "Neveljavni podatki",
        module_not_licensed: "Modul ni vključen za ta hotel",
        not_found: "Ni najdeno",
        on_add: "Napaka pri dodajanju",
        on_edit: "Napaka pri urejanju",
        on_load: "Napaka pri nalaganju",
        operation_in_progress: "Ta žig se že zapisuje. Ne pritiskaj znova — čez trenutek bo viden.",
        operation_mismatch: "Zaslon je zastarel. Osveži stran in poskusi znova.",
        session_expired: "Seja je potekla. Prijavi se znova — narejeno ni bilo nič.",
        staff_break_recording_off: "Hiša ne beleži odmorov.",
        staff_not_employed: "Za ta dan nimaš veljavne kadrovske kartice, zato žiga ni mogoče zapisati. Povej vodji ali kadrovski službi.",
        staff_time_busy: "Žig se že zapisuje. Poskusi znova čez trenutek.",
        staff_time_month_closed: "Ta mesec je zaključen in ga ni več mogoče spreminjati. Če gre za napako, se obrni na vodjo — ponovno odpre ga lahko samo kadrovska.",
        staff_time_out_of_range: "Čas žiga je izven dovoljenega okna.",
        staff_time_sequence: "Vrstni red žigov ni mogoč (npr. prihod na že odprt dan).",
        unauthorized: "Nimaš pravic za to dejanje",
    },
    general: {
        hint: 'Namig',
        cancel: "Prekliči",
        hide_password: "Skrij geslo",
        save: "Shrani",
        show_password: "Pokaži geslo",
    },
    home: {
        needs_you: "Čaka tvoj odgovor",
        no_shift_today: "Za danes ni objavljenega turnusa.",
        open_clock: "Vsi žigi",
        waiting_on_others: "Čaka odgovor hiše",
    },
    license: {
        contact: "Za vklop se obrni na sistemskega skrbnika. Podatki hiše ostanejo nedotaknjeni.",
        lead: "Modul »Zaposleni in čas« ni del naročnine te hiše, zato ta portal ne more prikazati ničesar.",
        switch_account: "Prijavi se z drugim računom",
        title: "Ta storitev ni vključena",
    },
    login: {
        demo_signing_in: "Prijavljam …",
        demo_failed: "Te vloge na tem strežniku ni.",
        failed: "Napačno uporabniško ime ali geslo",
        password: "Geslo",
        remember: "Ostani prijavljen",
        remember_hint: "Na svojem telefonu. Na tujem se raje ne prijavi trajno.",
        same_account: "Prijavi se z istim uporabniškim imenom in geslom kot v PMS. Gesla tu ni mogoče ponastaviti — za to se obrni na skrbnika.",
        submit: "Prijava",
        subtitle: "Moja ura, moj urnik, moj dopust in moji dokumenti.",
        title: "Prijava",
        username: "Uporabniško ime",
    },
    nav: {
        account: "Račun",
        clock: "Ura",
        documents: "Dokumenti",
        home: "Domov",
        language: "Jezik",
        language_de: "Deutsch",
        language_en: "English",
        language_hr: "Hrvatski",
        language_hu: "Magyar",
        language_it: "Italiano",
        language_sl: "Slovenščina",
        leave: "Dopust",
        logout: "Odjava",
        profile: "Moj profil",
        rota: "Urnik",
        theme: "Videz",
        theme_dark: "Temno",
        theme_light: "Svetlo",
        theme_system: "Sistemsko",
    },
    notifications: {
        channel_email: "E-pošta",
        channel_email_hint: "Potrebuje tvoj e-naslov.",
        channel_messaging: "Sporočilne aplikacije",
        channel_messaging_hint: "Potrebuje tvojo WhatsApp številko ali povezan Viber.",
        channel_push: "Aplikacija na telefonu",
        channel_push_hint: "Potrebuje službeni telefon ali uro, ki je seznanjena s teboj.",
        channel_sms: "SMS",
        channel_sms_hint: "Potrebuje tvojo telefonsko številko.",
    },
    profile: {
        account_hint: "Kdo si v sistemu.",
        account_managed_elsewhere: "Ime, uporabniško ime in geslo ureja skrbnik sistema. Tu jih ni mogoče spremeniti.",
        account_title: "Račun",
        card_hint: "Kaj ima hiša zapisano o tvojem delu.",
        card_read_only: "Kartico ureja kadrovska služba. Če kaj ne drži, ji to povej — popravek je nova kartica in ne popravljena stara.",
        card_title: "Moja kadrovska kartica",
        channel_unavailable: "Hiša te poti (še) nima nastavljene.",
        channels: "Poti obveščanja",
        channels_hint: "Nič izbranega pomeni vse poti, ki jih hiša ima. Siva pot pomeni, da je hiša ni nastavila.",
        contact_hint: "Kam naj hiša pošlje obvestilo o urniku. Ti podatki so tvoji in jih urejaš sam.",
        contact_title: "Moji podatki za obveščanje",
        department: "Oddelek",
        email: "E-naslov",
        email_hint: "Sem pride obvestilo o objavi urnika in o spremembi turnusa.",
        employee_no: "Interna številka",
        engagement: "Pravna podlaga",
        hours_per_week: "{hours} ur na teden",
        language_hint: "Velja za to napravo. Jezik na računu nastavi kadrovska služba in določa, v katerem jeziku se portal odpre prvič.",
        manager: "Nadrejeni",
        name: "Ime",
        no_card: "Za danes ni veljavne kartice.",
        no_card_hint: "To je običajno na prvi dan ali med sezonami. Če delaš, povej kadrovski.",
        period: "Obdobje",
        period_from: "od {date}",
        phone: "Telefon",
        phone_hint: "V mednarodni obliki: +38641123456.",
        phone_invalid: "Telefon mora biti v obliki +386… (od 8 do 15 števk).",
        position: "Delovno mesto",
        property: "Hiša",
        saved: "Shranjeno.",
        time_record: "Evidenca časa",
        time_record_not_required: "Ni obvezna",
        username: "Uporabniško ime",
        weekly_obligation: "Tedenska obveznost",
        work_pattern: "Razporeditev po dnevih",
        working_time: "Delovni čas",
    },
    staff: {
        acknowledge: "Potrjujem",
        acknowledge_failed: "Potrditve ni bilo mogoče zapisati.",
        acknowledge_hint: "S potrditvijo se v evidenco zapiše, da si spremembo videl, in kdaj. Dokler ne potrdiš, razporejevalec vidi, da še ne veš.",
        acknowledged: "Prejem potrjen",
        basis_agency_worker: "Agencijski delavec",
        basis_copyright_contract: "Avtorska pogodba",
        basis_employment_contract: "Pogodba o zaposlitvi",
        basis_other: "Druga pravna podlaga",
        basis_pensioner_temporary: "Začasno delo upokojenca",
        basis_self_employed: "Samostojni izvajalec",
        basis_service_contract: "Podjemna pogodba",
        basis_short_term_family_work: "Kratkotrajno družinsko delo",
        basis_student_work: "Študentsko delo",
        basis_traineeship: "Praktično usposabljanje ali vajeništvo",
        break_of: "odmor {minutes}",
        change_changed_at: "Spremenjeno {at}",
        change_moved: "Iz {previous} v {current}",
        change_reason_line: "Razlog: {reason}",
        change_withdrawn: "Turnus je odpovedan (bil je {previous})",
        day_friday: "pet",
        day_monday: "pon",
        day_saturday: "sob",
        day_sunday: "ned",
        day_thursday: "čet",
        day_tuesday: "tor",
        day_wednesday: "sre",
        decline: "Ne morem",
        decline_failed: "Odgovora ni bilo mogoče zapisati.",
        decline_hint: "Razporejevalec bo videl, da ne moreš, in poiskal nadomestilo. Sprememba ostane na seznamu, dokler tega ne uredi.",
        decline_note: "Zakaj ne moreš? (neobvezno)",
        decline_send: "Pošlji",
        decline_title: "Ne morem delati",
        declined: "Zabeleženo: ne moreš.",
        duration_fixed_term: "Določen čas",
        duration_indefinite: "Nedoločen čas",
        exempt_managing_person: "Poslovodna oseba",
        exempt_other: "Drug razlog",
        free_day: "Prosto",
        load_failed: "Podatkov ni bilo mogoče prebrati.",
        my_changes_title: "Spremembe, ki jih še nisi potrdil: {count}",
        my_pending_title: "Zahtevki, ki čakajo odločitev: {count}",
        my_rota: "Moj urnik",
        my_rota_empty: "Ta teden nimaš razporejenega turnusa.",
        my_rota_empty_hint: "Kar vidiš tukaj, je samo objavljeno. Osnutkov razporejevalca ne kaže — dokler teden ni objavljen, ni obljuba.",
        next_week: "Naslednji teden",
        no_swap_hint: "Menjave turnusa s sodelavcem in prošnje za nadomeščanje ta portal nima. Če turnusa ne moreš delati, pritisni »Ne morem« pri spremembi zgoraj — vodja to takoj vidi z razlogom vred. Za načrtovano odsotnost oddaj zahtevek za dopust.",
        previous_week: "Prejšnji teden",
        record_clock: "Žigov",
        record_manual: "Ročnega vnosa",
        record_pattern: "Delovnega vzorca",
        record_schedule: "Urnika",
        state_cancelled: "Odpovedano",
        state_draft: "Osnutek",
        state_published: "Objavljeno",
        this_week: "Ta teden",
        time_full: "Polni delovni čas",
        time_part: "Krajši delovni čas",
        today: "Danes",
        try_again: "Poskusi znova",
        unit_hours: "h",
        unit_minutes: "min",
    },
    staff_absence: {
        days_recorded: "Evidentiranih dni: {count}",
        decided_by_other: "O zahtevku odloči nekdo drug — svojega ne moreš potrditi sam.",
        from: "Od",
        kind: "Vrsta",
        kind_business_trip: "Službena pot",
        kind_holiday: "Praznik",
        kind_other: "Drugo",
        kind_sick_leave: "Bolniška",
        kind_training: "Izobraževanje",
        kind_unexcused: "Neopravičena odsotnost",
        kind_vacation: "Dopust",
        my_absences: "Moje odsotnosti",
        my_absences_hint: "Tvoji zahtevki in odločitve o njih. Zavrnjeni ostanejo vidni — zavrnitev, ki izgine, je zavrnitev, ki se ni zgodila.",
        no_my_requests: "Nimaš zahtevkov za odsotnost.",
        note: "Opomba",
        other_kinds_hint: "Zahtevati je mogoče samo dopust. Bolniško javiš vodji, ki jo vpiše v evidenco; praznik izhaja iz koledarja hiše; izobraževanje in službena pot sta napotitev delodajalca. Vse to se pojavi v tvoji evidenci, ko je vpisano.",
        other_kinds_title: "Druge odsotnosti",
        range_backwards: "Konec ne more biti pred začetkom.",
        range_too_long: "Zahtevek lahko obsega največ {days} dni. Za daljšo odsotnost vpiše dneve vodja.",
        request_hint: "Zase lahko zahtevaš dopust. Bolniška se prijavi in evidentira, praznik izhaja iz koledarja, izobraževanje in službeno pot vpiše vodja.",
        request_leave: "Zahtevaj dopust",
        request_sent: "Zahtevek je poslan v odločitev.",
        send_request: "Pošlji zahtevek",
        source_calendar: "Koledar praznikov",
        source_manager: "Vnos vodje",
        source_request: "Zahtevek",
        state_approved: "Odobreno",
        state_rejected: "Zavrnjeno",
        state_requested: "Zahtevano",
        state_withdrawn: "Umaknjeno",
        to: "Do",
        total_absence: "Odsotnost",
        withdraw: "Umakni",
        withdrawn: "Zahtevek je umaknjen.",
    },
    staff_document: {
        download: "Prenesi",
        expired_on: "Poteklo {date}",
        expires_on: "Velja do {date}",
        kind_aneks: "Aneks",
        kind_drugo: "Drugo",
        kind_m1: "Obrazec M-1",
        kind_nda: "Izjava o zaupnosti",
        kind_odlok: "Odlok",
        kind_pogodba: "Pogodba o zaposlitvi",
        my_documents: "Moji dokumenti",
        my_documents_hint: "Kar je hiša vložila k tebi. Bereš in preneseš; vlaga hiša.",
        no_my_documents: "Zate še ni vloženega nobenega dokumenta.",
        read_only_hint: "Dokumentov tu ni mogoče dodajati ali brisati — mapa je zapis delodajalca. Potrditev prejema ima samo odlok o dopustu.",
    },
    staff_month: {
        badge_closed: "Zaključeno",
    },
    staff_time: {
        break_counts_hint: "Odmor šteje v delovni čas in se ne odšteva.",
        break_short: "odmor {minutes}",
        correction_approved: "Potrjen",
        correction_date: "Datum",
        correction_decided_by_other: "O zahtevku odloči nekdo drug — svojega ne moreš potrditi sam.",
        correction_hint: "Zahtevaš lahko sam; odloči nekdo drug.",
        correction_kind: "Vrsta žiga",
        correction_mode_existing: "Popravek obstoječega žiga",
        correction_mode_missing: "Manjkajoči žig",
        correction_no_stamps: "Ta dan nima žiga, ki bi ga bilo mogoče popraviti.",
        correction_reason: "Razlog",
        correction_rejected: "Zavrnjen",
        correction_requested: "Zahtevan",
        correction_send: "Pošlji zahtevek",
        correction_sent: "Zahtevek je poslan v odločitev.",
        correction_time: "Ura",
        correction_what: "Kaj popravljaš",
        correction_which_stamp: "Kateri žig",
        day_open: "Dan ni zaključen — odhod ni zabeležen.",
        decision_reason: "Razlog: {reason}",
        excess_pending: "Presežek — čaka razvrstitev",
        first_in: "prihod {at}",
        kind_break_end: "Konec odmora",
        kind_break_start: "Odmor",
        kind_clock_in: "Prihod",
        kind_clock_out: "Odhod",
        last_out: "odhod {at}",
        month_pdf: "Mesečni izpis (PDF)",
        my_corrections: "Moji zahtevki za popravek",
        my_record: "Moja evidenca",
        my_time: "Moja ura",
        next_month: "Naslednji mesec",
        no_days: "V tem obdobju ni zabeleženih dni.",
        of_which_break: "od tega odmor {minutes}",
        open: "Nedokončan",
        period_month: "Mesec",
        period_week: "Teden",
        previous_month: "Prejšnji mesec",
        queued: "Čaka na povezavo: {count}",
        refused: "Žig ni bil sprejet.",
        not_recorded: "Žig ni zabeležen: {reason}",
        request_correction: "Zahtevaj popravek",
        stamped: "Zabeleženo: {kind}",
        this_month: "Ta mesec",
        today_total: "Danes",
        total_balance: "Saldo",
        total_break: "Odmor",
        total_expected: "Obveznost",
        total_worked: "Delo",
    },
    staff_vacation: {
        acknowledge: "Potrjujem",
        acknowledge_hint: "Potrjuješ PREJEM odloka, ne strinjanja s številom dni.",
        acknowledged: "Potrjeno",
        acknowledged_toast: "Prejem odloka je zabeležen.",
        available: "Na voljo",
        bucket_current: "Nov",
        bucket_old: "Star",
        current_leave: "Letni dopust {year}",
        days_count: "{count} dni",
        decision_for_year: "Odlok za leto {year}",
        decision_total: "Odmerjeno: {days}",
        entitled: "Odmerjeno",
        expired_days: "Zapadlo {days} — rok je bil {date}.",
        issued_at: "Izdal: {who}, {at}",
        my_balance: "Moj dopust",
        my_decisions: "Moji odloki",
        my_decisions_hint: "Odločbe o odmeri letnega dopusta, ki jih je hiša izdala tebi.",
        next_year: "Naslednje leto",
        no_balance: "Za to leto ni podatka o dopustu.",
        no_balance_hint: "Saldo nastane, ko je izdan odlok in ko ima oseba veljavno obdobje dela.",
        no_my_decisions: "Zate še ni izdan noben odlok.",
        no_usage: "V tem letu ni zabeležene izrabe dopusta.",
        not_acknowledged: "Ni potrjeno",
        not_issued_hint: "Odlok za to leto še ni izdan.",
        of_which_pending: "Od tega čaka odločitev: {days}",
        of_which_used: "Od tega izrabljeno: {days}",
        old_leave: "Star dopust ({year})",
        over_remaining: "Zahtevek presega razpoložljivih {days}. Odločitev je vseeno na hiši.",
        part_base: "Osnova",
        part_children: "Otroci",
        part_education: "Izobrazba",
        part_full_year: "Skupaj (celo leto)",
        part_responsibility: "Odgovorno delovno mesto",
        part_special: "Posebna kategorija",
        part_tenure: "Delovna doba",
        pdf: "PDF",
        pending: "V odločanju",
        previous_year: "Prejšnje leto",
        requested_days: "Zahtevanih delovnih dni: {days}",
        still_days: "še {days}",
        still_of: "še {left} od {total}",
        this_year: "Letos",
        usage_history: "Zgodovina uporabe",
        use_by: "Izrabi do {date}.",
        used: "Izrabljeno",
    },
    success: {
        add: "Uspešno dodano",
        edit: "Uspešno posodobljeno",
    },
    validation: {
        email: "Neveljaven e-naslov",
        required: "To polje je obvezno",
    },
}
